import { Shield, AlertTriangle, RefreshCw, CheckCircle } from "lucide-react";

interface PaymentAuthenticationViewProps {
  clientSecret: string;
  onAuthenticate: (clientSecret: string) => void;
  processing: boolean;
  error: string | null;
  onRetry: () => void;
}

export function PaymentAuthenticationView({
  clientSecret,
  onAuthenticate,
  processing,
  error,
  onRetry,
}: PaymentAuthenticationViewProps) {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Shield className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Payment Authentication Required
          </h1>
          <p className="text-lg text-gray-600 max-w-md mx-auto">
            Your payment requires additional authentication to complete the
            reservation.
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 px-6 py-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Security Verification
            </h2>
          </div>

          <div className="p-8">
            {/* Description */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Complete Authentication
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Please complete the authentication process to confirm your
                payment. This is a security measure to protect your transaction.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-8 bg-red-50 border border-red-200 rounded-xl p-6">
                <div className="flex items-start">
                  <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                    <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-800 mb-1">
                      Authentication Error
                    </h4>
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Security Notice */}
            <div className="bg-blue-50 rounded-xl p-6 mb-8 border border-blue-200">
              <div className="flex items-start">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
                  <Shield className="w-3 h-3 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-blue-800 mb-1">
                    Secure Authentication
                  </h4>
                  <p className="text-sm text-blue-600">
                    This process is handled securely by your bank and typically
                    takes just a few moments. You may be redirected to your
                    bank&apos;s website or mobile app.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => onAuthenticate(clientSecret)}
                disabled={processing}
                className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transform hover:scale-[1.02] flex items-center justify-center"
              >
                {processing ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
                    Processing Authentication...
                  </>
                ) : (
                  <>
                    <Shield className="h-5 w-5 mr-2" />
                    Complete Authentication
                  </>
                )}
              </button>

              <button
                onClick={onRetry}
                disabled={processing}
                className="flex-1 bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold text-lg hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Refresh Status
              </button>
            </div>

            {/* Additional Info */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                Having trouble? Contact support for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
